(ns practitest.frontend.forms3.testset
  (:require [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.permissions :as permissions]
            [practitest.frontend.common-entities.events :as common-events]
            [practitest.frontend.common-entities.delete-confirmation-modal :as delete-confirmation-modal]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components2.test-type :as test-type]
            [practitest.frontend.components.grid.subs :as grid.subs]
            [practitest.frontend.forms3.entity-form :as f3.entity-form]
            [practitest.frontend.forms3.entity-form.forms :as f3.forms]
            [practitest.frontend.common.i18n :refer [format-translation get-translation] :as i18n]
            [practitest.frontend.kanban :as kanban]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.routing.router :as router]
            [practitest.frontend.testset2.subs :as testset.subs]
            [practitest.frontend.subs :as subs]
            [re-frame.core :as re-frame]
            [practitest.frontend.common.re-frame :refer [partial-dispatch]]
            [practitest.frontend.common-entities.metadata :as metadata]))

;; Testset-specific code for forms version 3 support
(def ^:private current-entity :testset)
(def metadata (get metadata/entities-metadata current-entity))
(def delete-permission (:delete-permissions metadata))

(defn build-testset-tabs
  [instances-page linked-issues-page edit?]
  [(f3.entity-form/general-entity-tab)

   (when edit?
     {:tab-id    :instances
      :tab-title [:span.text-nowrap
                  (format-translation
                    (get-translation "testset.tabs.instances" "Instances (%{n})")
                    :n (or @(re-frame/subscribe [::testset.subs/testset-instances-total-count])
                           "⋯"))]
      :tab-view  instances-page})

   (when edit?
     {:tab-id    :linked-issues
      :tab-title [:span.text-nowrap
                  (format-translation
                    (get-translation "testset.tabs.linked-issues" "Linked Issues (%{n})")
                    :n (or (get-in @(re-frame/subscribe [::subs/entity :testset]) [:current :linked_issues_count])
                           @(re-frame/subscribe [::testset.subs/linked-issues-total-count])
                           "⋯"))]
      :tab-view  linked-issues-page})

   (when edit?
     (f3.entity-form/history-tab))])

(defn testset-menu []
  (let [testset (:current @(re-frame/subscribe [::subs/entity :testset]))
        testset-id (:id testset)
        testset-display-id (:display_id testset)
        path-params {:id testset-id}

        clone-testset-uri (broutes/get-url-view "suites#clone_entity" path-params)
        clone-failed-testset-uri (broutes/get-url-view "suites#clone_entity_with_failed_tests" path-params)
        suite-instances-report-uri (broutes/get-url-view "reports#index" path-params {"suite_display_id" testset-display-id "report_type_name" "tabular"})
        suite-instances-steps-report-uri (broutes/get-url-view "reports#index" path-params {"suite_display_id" testset-display-id "report_type_name" "tabular-with-steps"})
        print-testset-uri (broutes/get-url-view "suites#print" path-params {"ids" testset-id})
        delete-permission? (permissions/has-permission? delete-permission)]
    [{:text     (get-translation "entity.button.delete" "Delete")
      :test-id  "testset-form-menu-delete"
      :disabled? (not delete-permission?)
      :on-click (fn [e]
                  (when delete-permission?
                    (dom/prevent-default e)
                    (delete-confirmation-modal/open! current-entity)))
      :mixpanel ["Form Actions" {"Entity" "Testset" "Action" "Delete entity"}]
      :icon     [icons/mux-trash]}
     {:text     (get-translation "generic.button.clone" "Clone")
      :test-id  "testset-form-menu-clone"
      :icon     [icons/mux-copy]
      :mixpanel    ["Form Actions" {"Entity" "Testset" "Action" "Clone entity"}]
      :on-click (fn [e]
                  (dom/prevent-default e)
                  (dom/post-navigate! clone-testset-uri))}
     {:text     (get-translation "testset.button.clone-failed" "Clone failed instances only")
      :test-id  "testset-form-menu-clone-failed-only"
      :icon     [icons/mux-square]
      :on-click (fn [e]
                  (dom/prevent-default e)
                  (dom/post-navigate! clone-failed-testset-uri))}
     {:text        (get-translation "generic.button.print" "Print")
      :test-id     "testset-form-menu-print"
      :icon        [icons/mux-printer]
      :mixpanel    ["Form Actions" {"Entity" "Testset" "Action" "Print entity"}]
      :new-window? true
      :href        print-testset-uri}
     {:text        (get-translation "testset.button.instances-report" "Instances Tabular Summary Report")
      :test-id     "testset-form-menu-instances-report"
      :icon        [icons/suite-instances-report]
      :new-window? false
      :href        suite-instances-report-uri
      :mixpanel    ["Instance Tabular Summary report" {"Entity" "Testset"}]}
     {:text        (get-translation "testset.bottom-bar.instances-steps-report-button" "Instances Tabular with Steps Report")
      :test-id     "testset-index-grid-menu-instances-steps-report"
      :icon        [icons/suite-instances-report]
      :new-window? false
      :href        suite-instances-steps-report-uri
      :mixpanel    ["Instance Tabular with Steps report" {"Entity" "Testset"}]}]))

;; New layout calculation function
(defn form-layout-testset
  [xbot? new? entity-fields]
  (let [kanban-enabled? @(re-frame/subscribe [::subs/kanban-enabled?])
        nil-or-unavailable? (fn [field]
                           (or (nil? field)
                               (not (contains? entity-fields field))))
        initial-layout {:title {:fields [{:field :name}]}
                        (:description f3.entity-form/sections-keys) {:fields [{:field :description}]}}

        existing-fields (set (f3.forms/flatten-layout initial-layout))
        edit-only-fields #{:run_status :last_run :estimated_duration :actual_duration
                           :created_at :updated_at :author}
        xbot-only-fields #{:client_id}
        to-remove? (fn [field]
                     (or (nil-or-unavailable? field)
                         (contains? existing-fields field)
                         (and (not xbot?) (contains? xbot-only-fields field))
                         (and new? (contains? edit-only-fields field))))

        filtered-entity-fields (cond-> (f3.entity-form/filter-entity-fields entity-fields to-remove?)
                                       kanban-enabled? (assoc :kanban_lane kanban/kanban-field))
        filtered-entity-fields-flat (vec (keys filtered-entity-fields))
        entity-sections @(re-frame/subscribe [::subs/entity-sections :testset])

        update-main-sections (if (some? entity-sections)
                               #(f3.entity-form/update-layout % entity-sections filtered-entity-fields)
                               #(f3.entity-form/update-layout % (:general-info f3.entity-form/sections-keys) filtered-entity-fields-flat "general_info" entity-fields))

        layout (-> initial-layout
                   (update-main-sections))]
    layout))


(defmethod f3.forms/build-entity-form-layout :testset
  [_ new? _ entity-fields entity]
  (let [xbot? (:has_xbot_instances entity)]
    (form-layout-testset xbot? new? entity-fields)))

;; Almost same for now, so keeping them in this file
(defn main [instances-page linked-issues-page new?]
  (let [entity (:current @(re-frame/subscribe [::subs/entity :testset]))
        form-id :testset
        xbot? (:has_xbot_instances entity)
        expanded? @(re-frame/subscribe [::grid.subs/expanded? :testset :instances-state])
        query-params (router/get-current-query-params)
        cloned? (:cloned query-params)]
    [:<>
     [delete-confirmation-modal/delete-confirmation-modal
      {:entity current-entity
       :title (get-translation "testset.confirm.delete.title" "Delete Test Set?")
       :entity-name (get-translation "testset.singular" "Test Set")
       :on-confirm #(re-frame/dispatch [::common-events/delete-current-entity current-entity])}]
     [f3.entity-form/entity-form
      {:new? new?
      :expanded? expanded?
      :popup-menu (testset-menu)
      :entity-type :testset
      :form-id form-id
      :breadcrumbs [{:href (broutes/get-url-view "suites#index")
                     :label (get-translation "navigation.project-menu.testsets" "Test sets & Runs")}]
      :on-save (partial-dispatch [::common-events/save-entity form-id :suite])
      :header-tabs (build-testset-tabs instances-page linked-issues-page (not new?))
      :xbot? xbot?
      :default-tab (if (or cloned? new?)
                     :general
                     :instances)
      :edit-permissions? (permissions/has-permission? "sets_editor")
      :customization-permissions? (permissions/has-permission? "customization")
      :general-form-inner-tabs {:new #{:attachments}
                                :edit #{:attachments :comments}}
      :default-active-sub-tab :attachments}]]))
